import { Markdown, Themes } from '@mgmresorts/markdown';
import { Icon } from '@mgmresorts/mgm-ui';
import React, { useEffect, useRef, useState, useCallback } from 'react';

import { generateAndStoreStepUpState } from '../../../api/fetch/stepup';
import { useAroIdMeContent } from '../../../common/api';
import { useContent } from '../../../hooks/useContent';
import { captureEvent } from '../../../monitoring/logrocket';
import { useAnalyticsService } from '../../../utils/analytics/analytics';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { IDENTITY_WIDGET_BASEPATH, IDENTITY_WIDGET_PATHS } from '../../../utils/constants';
import { getOrigin } from '../../../utils/url';

import {
  StyledIDMeModalOverlay,
  StyledIDMeModal,
  StyledIDMeHeader,
  StyledIDMeTitle,
  StyledIDMeContent,
  StyledIDMeDescription,
  StyledIDMeLegal,
  StyledIDMeFooter,
  StyledIDMeFooterButton,
  StyledIDMeCloseButton,
} from './styles';

export interface VerifyIdentityModalProps {
  isOpen: boolean;
  stepupContextId: string;
  onClose: () => void;
  onUseDifferentAccount: () => void;
  testId?: string;
}

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));

export function VerifyIdentityModal(props: VerifyIdentityModalProps) {
  if (!props.isOpen) return null;
  return <VerifyIdentityModalContent {...props} />;
}

function VerifyIdentityModalContent({
  isOpen,
  stepupContextId,
  onClose,
  onUseDifferentAccount,
  testId = 'verify-identity-modal',
}: VerifyIdentityModalProps) {
  const { getContentByPath } = useContent();
  const { authClient } = useAuthContext();
  const verifyContent = useAroIdMeContent({ skip: !isOpen });
  const modalRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const verifyCTA = isVerifying ? getContentByPath<string>('loadingCTA.label') : verifyContent.primaryCtaLabel;

  const trackEvent = useEvent();

  type PageContext = { name: string; destinationUrl: string; language: string };
  const previousPageRef = useRef<PageContext | null>(null);

  if (previousPageRef.current === null && typeof window !== 'undefined') {
    const win = window as unknown as { digitalData?: { page?: PageContext } };
    if (win.digitalData?.page) {
      previousPageRef.current = { ...win.digitalData.page };
    }
  }

  const { setDigitalData } = useAnalyticsService('page');

  usePageLoadEvent('identity:mfa', 'id me verification modal');

  const handleUseDifferentAccount = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Use a different account', 'unifiedmfactas', {}, true);
    captureEvent('Verify ID.me Modal - Use Different Account Clicked', { stepupContextId });
    if (previousPageRef.current) {
      setDigitalData('page', previousPageRef.current);
    }
    onUseDifferentAccount();
  }, [onUseDifferentAccount, trackEvent, stepupContextId, setDigitalData]);

  useEffect(() => {
    if (!isOpen) setIsVerifying(false);
  }, [isOpen]);

  // Wrapped actions to dispatch LogRocket telemetry when buttons are clicked
  const handleClose = useCallback(() => {
    trackEvent('identity – unified MFA CTAs', 'close id me verification modal', 'unifiedmfactas');
    captureEvent('Verify ID.me Modal - Close Clicked', { stepupContextId });
    if (previousPageRef.current) {
      setDigitalData('page', previousPageRef.current);
    }
    onClose();
  }, [onClose, trackEvent, stepupContextId, setDigitalData]);

  useEffect(() => {
    if (!isOpen) return undefined;
    // Track when the modal is successfully shown (Impression Milestone)
    captureEvent('Verify ID.me Modal - Shown', { stepupContextId });

    previousFocusRef.current = document.activeElement as HTMLElement;
    const focusFrame = requestAnimationFrame(() => headingRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusable = getFocusableElements(modalRef.current);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === headingRef.current) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

  // When the user navigates to ID.me and then hits the browser Back button, the
 // browser restores this page from the back-forward cache (bfcache) instead of
// reloading it. This means the React state is frozen exactly as it was before
// redirecting (with the modal open and the verification button stuck in loading).
// The pageshow event's `persisted` flag tells us this happened, so we can:
// 1. Reset the verification loading state to un-stick the button.
// 2. Call `handleClose()` to close the modal (returning the user back to the guided
//    sign-in page) and cleanly restore parent-page analytics (digitalData).
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsVerifying(false);
        handleClose();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('pageshow', handlePageShow);
      document.body.style.overflow = 'unset';
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, handleClose, stepupContextId]);

  if (!isOpen) return null;

  const handleVerifyWithIdMe = async () => {
    if (isVerifying) return;
    setIsVerifying(true);

    await trackEvent('identity – unified MFA CTAs', 'verify with id me', 'unifiedmfactas', {}, true);
    // Track when user triggers the primary verification CTA (Conversion Milestone)
    captureEvent('Verify ID.me Modal - Verify Clicked', { stepupContextId });

    const state = generateAndStoreStepUpState();
    const redirectUri = `${getOrigin()}${IDENTITY_WIDGET_BASEPATH}${IDENTITY_WIDGET_PATHS.AroIdMeVerification}`;
    authClient.initiateAROVerification(stepupContextId, redirectUri, state);
  };

  const titleId = `${testId}-title`;
  const descriptionId = `${testId}-description`;

  return (
    <StyledIDMeModalOverlay data-testid={`${testId}-overlay`}>
      <StyledIDMeModal
        ref={modalRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-testid={testId}
      >
        <StyledIDMeHeader>
          <StyledIDMeTitle id={titleId} ref={headingRef} tabIndex={-1} variant='heading-medium-1' as='h2'>
            {verifyContent.title}
          </StyledIDMeTitle>
        </StyledIDMeHeader>

        <StyledIDMeContent>
          <StyledIDMeDescription id={descriptionId} variant='body-regular-m'>
            {verifyContent.subtitle}
          </StyledIDMeDescription>

          <StyledIDMeLegal data-testid={`${testId}-legal`}>
            <Markdown theme={Themes.VEGA} addContainer={false} component={{ a: { variant: 'small' } }}>
              {verifyContent.body}
            </Markdown>
          </StyledIDMeLegal>
        </StyledIDMeContent>

        <StyledIDMeFooter>
          <StyledIDMeFooterButton
            variant='tertiary'
            size='large'
            onClick={handleUseDifferentAccount}
            testId={`${testId}-different-account-button`}
            label={verifyContent.secondaryCtaLabel}
          />
          <StyledIDMeFooterButton
            variant='primary'
            size='large'
            onClick={handleVerifyWithIdMe}
            testId={`${testId}-verify-button`}
            label={verifyCTA}
            disabled={isVerifying}
            loading={isVerifying}
          />
        </StyledIDMeFooter>

        <StyledIDMeCloseButton
          onClick={handleClose}
          aria-label={getContentByPath<string>('verifyIdentity.closeLabel')}
          data-testid={`${testId}-close-button`}
        >
          <Icon name='symbol-x' variant='outlined' size='small' aria-hidden='true' />
        </StyledIDMeCloseButton>
      </StyledIDMeModal>
    </StyledIDMeModalOverlay>
  );
}

VerifyIdentityModal.displayName = 'VerifyIdentityModal';
